import React from "react";
import { Check } from "lucide-react";

/**
 * Horizontal process wizard — outline round steps (not filled).
 * Only the active step gets the strong highlight ring.
 * accent: "payroll" | "attendance"
 * viewLocked: month processed/finalised — navigate freely; mute non-actionable steps.
 * actionableKeys: steps that stay editable when locked (process / finalise) — opacity only.
 */
export default function ProcessWizardStrip({
  steps = [],
  activeKey,
  visitedKeys = new Set(),
  onSelect,
  accent = "payroll",
  darkMode = false,
  statusLabel = "",
  viewLocked = false,
  actionableKeys = null,
}) {
  const accentRing =
    accent === "attendance"
      ? "border-teal-600 text-teal-700 dark:border-teal-400 dark:text-teal-300"
      : "border-indigo-600 text-indigo-700 dark:border-indigo-400 dark:text-indigo-300";
  const accentActive =
    accent === "attendance"
      ? "border-teal-600 text-teal-800 ring-2 ring-teal-500/35 dark:border-teal-300 dark:text-teal-100"
      : "border-indigo-600 text-indigo-800 ring-2 ring-indigo-500/35 dark:border-indigo-300 dark:text-indigo-100";
  const accentDone =
    accent === "attendance"
      ? "border-teal-500/70 text-teal-600 dark:border-teal-500/60 dark:text-teal-300"
      : "border-indigo-500/70 text-indigo-600 dark:border-indigo-500/60 dark:text-indigo-300";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const actionSet =
    actionableKeys instanceof Set
      ? actionableKeys
      : Array.isArray(actionableKeys)
        ? new Set(actionableKeys)
        : null;

  const activeIdx = Math.max(
    0,
    steps.findIndex((s) => s.key === activeKey)
  );

  return (
    <div className="flex min-w-0 items-center gap-2">
      {statusLabel ? (
        <span
          className={`shrink-0 text-[11px] font-bold uppercase tracking-[0.1em] ${
            accent === "attendance" ? "text-teal-700 dark:text-teal-300" : "text-indigo-700 dark:text-indigo-300"
          }`}
        >
          {statusLabel}
        </span>
      ) : null}
      <ol className="flex min-w-0 flex-1 items-center gap-0 overflow-x-auto admin-shell-scrollbar py-0.5">
        {steps.map((s, idx) => {
          const active = s.key === activeKey;
          const statusPassed = ["passed", "acknowledged"].includes(String(s.status || ""));
          // Locked month: steps before the current one read as done (check), not as a second “active”.
          const showCheck = statusPassed || (viewLocked && idx < activeIdx);
          const connectorOn = showCheck || active || visitedKeys.has(s.key) || viewLocked;
          const failed = !viewLocked && s.status === "failed";
          const actionable = !viewLocked || !actionSet || actionSet.has(s.key);
          const clickable = viewLocked || visitedKeys.has(s.key) || active || statusPassed;
          return (
            <li key={s.key} className="flex shrink-0 items-center">
              {idx > 0 ? (
                <span
                  className={`mx-1 h-px w-3 sm:w-4 ${
                    connectorOn
                      ? accent === "attendance"
                        ? "bg-teal-500/50"
                        : "bg-indigo-500/50"
                      : "bg-[hsl(var(--ds-border))]"
                  }`}
                  aria-hidden
                />
              ) : null}
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onSelect?.(s.key)}
                title={
                  viewLocked && !actionable
                    ? `${s.label} (view only)`
                    : s.label
                }
                className={`group flex max-w-[8.5rem] flex-col items-center gap-0.5 disabled:opacity-40 ${
                  clickable ? "cursor-pointer" : "cursor-default"
                } ${viewLocked && !actionable ? "opacity-70" : ""}`}
              >
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full border-2 bg-transparent text-[11px] font-bold transition ${
                    active
                      ? accentActive
                      : failed
                        ? "border-[hsl(var(--ds-danger))] text-[hsl(var(--ds-danger))]"
                        : showCheck
                          ? accentDone
                          : visitedKeys.has(s.key)
                            ? accentRing
                            : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
                  }`}
                >
                  {showCheck ? <Check size={12} strokeWidth={2.5} /> : idx + 1}
                </span>
                <span
                  className={`w-full truncate text-center text-[11px] font-semibold leading-tight ${
                    active
                      ? accent === "attendance"
                        ? "text-teal-800 dark:text-teal-100"
                        : "text-indigo-800 dark:text-indigo-100"
                      : muted
                  }`}
                >
                  {s.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
